import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import BildAuswahl from '../components/BildAuswahl';

// ─── Vordefinierte Formularfelder ─────────────────────────────────────────
const PRESET_FELDER = [
  { id: 'teilnehmer_name',       label: 'Teilnehmer (abweichend)',      typ: 'teilnehmer', pflicht: false,
    hinweis: 'Vor- und Nachname des Teilnehmers, wenn abweichend von der Kontaktperson' },
  { id: 'teilnehmer_geburtsdatum', label: 'Geburtsdatum (Teilnehmer)',  typ: 'date',    pflicht: false },
  { id: 'teilnehmer_geschlecht', label: 'Geschlecht (Teilnehmer)',      typ: 'select',  pflicht: false,
    optionen: ['Männlich','Weiblich','Divers'] },
  { id: 'ist_mitglied',          label: 'Vereinsmitglied?',             typ: 'boolean', pflicht: false },
  { id: 'schwimmerfahrung',      label: 'Schwimmerfahrung',             typ: 'select',  pflicht: false,
    optionen: ['Keine','Anfänger','Fortgeschrittene','Leistungsschwimmer'] },
  { id: 'schwimmabzeichen',      label: 'Letztes Schwimmabzeichen',     typ: 'select',  pflicht: false,
    optionen: ['Keins','Seepferdchen','Trixi','Bronze','Silber','Gold'] },
  { id: 'ernaehrung',            label: 'Ernährungsbesonderheiten',     typ: 'text',    pflicht: false },
  { id: 'gesundheit',            label: 'Gesundheitliche Hinweise',     typ: 'textarea',pflicht: false },
  { id: 'notfallkontakt',        label: 'Notfallkontakt (Name & Tel.)', typ: 'text',    pflicht: false },
  { id: 'kommentar',             label: 'Anmerkungen',                  typ: 'textarea',pflicht: false },
];

const TYP_LABEL = { text: 'Text', textarea: 'Textbox', select: 'Auswahl', boolean: 'Ja/Nein', date: 'Datum' };
const STEPS = ['Basis', 'Termine', 'Kapazität & Preis', 'Formular'];

const INIT_FORM = {
  typ_id: '', titel: '', untertitel: '', beschreibung: '', ort: '', kursleiter: '',
  preis_mitglied: '', preis_gast: '', zahlungshinweis: '',
  max_teilnehmer: '', min_alter: '', max_alter: '',
  warteliste_aktiv: true, status: 'entwurf', aktiv: true,
  email_vorlage_id: '',
};

const INIT_TERMIN = { datum: '', uhrzeit_von: '', uhrzeit_bis: '', bemerkung: '' };

export default function AngebotForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'neu');

  const [step, setStep] = useState(0);
  const [typen, setTypen] = useState([]);
  const [emailVorlagen, setEmailVorlagen] = useState([]);
  const [form, setForm] = useState(INIT_FORM);
  const [termine, setTermine] = useState([]);
  const [felder, setFelder] = useState(() =>
    PRESET_FELDER.map(f => ({ ...f, aktiv: false }))
  );
  const [customFelder, setCustomFelder] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [bildPfad, setBildPfad] = useState('');
  const [bildDialog, setBildDialog] = useState(false);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [rt, rv] = await Promise.all([
          api.get('/api/admin/angebot-typen'),
          api.get('/api/admin/email-vorlagen'),
        ]);
        setTypen(rt.data);
        setEmailVorlagen(rv.data.filter(v => v.aktiv));
      } catch { /* ignore */ }
    };
    loadMeta();

    if (isEdit) {
      api.get(`/api/admin/angebote/${id}`).then(r => {
        const a = r.data;
        setForm({
          typ_id: a.typ_id || '', titel: a.titel, untertitel: a.untertitel || '',
          beschreibung: a.beschreibung || '', ort: a.ort || '', kursleiter: a.kursleiter || '',
          preis_mitglied: a.preis_mitglied || '', preis_gast: a.preis_gast || '',
          zahlungshinweis: a.zahlungshinweis || '',
          max_teilnehmer: a.max_teilnehmer || '', min_alter: a.min_alter || '', max_alter: a.max_alter || '',
          warteliste_aktiv: a.warteliste_aktiv, status: a.status, aktiv: a.aktiv,
          email_vorlage_id: a.email_vorlage_id || '',
        });
        setTermine((a.termine || []).map(t => ({
          datum: t.datum?.slice(0, 10) || '',
          uhrzeit_von: t.uhrzeit_von?.slice(0, 5) || '',
          uhrzeit_bis: t.uhrzeit_bis?.slice(0, 5) || '',
          bemerkung: t.bemerkung || '',
        })));

        // Felder rekonstruieren
        const gespeichert = a.formular_felder || [];
        const presetIds = PRESET_FELDER.map(f => f.id);
        const presetMapped = PRESET_FELDER.map(p => {
          const found = gespeichert.find(g => g.id === p.id);
          return found ? { ...p, aktiv: true, pflicht: found.pflicht, optionen: found.optionen || p.optionen } : { ...p, aktiv: false };
        });
        setFelder(presetMapped);
        setCustomFelder(gespeichert.filter(f => !presetIds.includes(f.id)));
        setBildPfad(a.bild_pfad || '');
        setLoading(false);
      }).catch(() => { setError('Angebot konnte nicht geladen werden'); setLoading(false); });
    }
  }, [id, isEdit]);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // Fehler bewusst nicht abfangen – BildAuswahl zeigt sie im Dialog an.
  const handleBildAuswahl = async (bildId) => {
    const r = await api.put(`/api/admin/angebote/${id}/bild`, { bild_id: bildId });
    setBildPfad(r.data.bild_pfad || '');
    setBildDialog(false);
  };

  // ─── Termine ─────────────────────────────────────────────────────────────
  const addTermin = () => setTermine(p => [...p, { ...INIT_TERMIN }]);
  const removeTermin = (i) => setTermine(p => p.filter((_, idx) => idx !== i));
  const setTermin = (i, key, val) => setTermine(p => p.map((t, idx) => idx === i ? { ...t, [key]: val } : t));

  // ─── Formular-Baukasten ───────────────────────────────────────────────────
  const toggleFeld = (id) => setFelder(p => p.map(f => f.id === id ? { ...f, aktiv: !f.aktiv } : f));
  const setFeldPflicht = (id, val) => setFelder(p => p.map(f => f.id === id ? { ...f, pflicht: val } : f));

  const addCustomFeld = () => setCustomFelder(p => [...p, {
    id: `custom_${Date.now()}`, label: '', typ: 'text', pflicht: false, optionen: [], aktiv: true,
  }]);
  const removeCustomFeld = (id) => setCustomFelder(p => p.filter(f => f.id !== id));
  const setCustomFeld = (id, key, val) => setCustomFelder(p => p.map(f => f.id === id ? { ...f, [key]: val } : f));

  // ─── Speichern ───────────────────────────────────────────────────────────
  const buildFormularFelder = () => [
    ...felder.filter(f => f.aktiv).map(({ aktiv: _, hinweis: __, ...rest }) => rest),
    ...customFelder.filter(f => f.label.trim()),
  ];

  const handleSave = async () => {
    if (!form.titel.trim()) { setStep(0); setError('Titel ist Pflicht'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        typ_id: form.typ_id || null,
        email_vorlage_id: form.email_vorlage_id || null,
        preis_mitglied: form.preis_mitglied || null,
        preis_gast: form.preis_gast || null,
        max_teilnehmer: form.max_teilnehmer || null,
        min_alter: form.min_alter || null,
        max_alter: form.max_alter || null,
        reihenfolge: 0,
        formular_felder: buildFormularFelder(),
        termine: termine.filter(t => t.datum).map((t, i) => ({ ...t, reihenfolge: i })),
      };
      if (isEdit) {
        await api.put(`/api/admin/angebote/${id}`, payload);
        navigate(`/angebote/${id}`);
      } else {
        const r = await api.post('/api/admin/angebote', payload);
        navigate(`/angebote/${r.data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <div className="page-header">
        <h2 className="page-title">{isEdit ? 'Angebot bearbeiten' : 'Neues Angebot'}</h2>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(isEdit ? `/angebote/${id}` : '/angebote')}>Abbrechen</button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Stepper */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {STEPS.map((s, i) => (
          <button key={s} type="button" onClick={() => setStep(i)}
            style={{
              flex: 1, padding: '0.6rem 0.5rem', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
              background: step === i ? 'var(--primary)' : step > i ? 'var(--primary-light)' : '#fff',
              color: step === i ? '#fff' : step > i ? 'var(--primary)' : 'var(--text-muted)',
              borderRight: i < STEPS.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
            <span style={{ marginRight: 4, opacity: 0.6 }}>{i + 1}.</span>{s}
          </button>
        ))}
      </div>

      {/* ─── Step 0: Basis ──────────────────────────────────────────────── */}
      {step === 0 && (
        <div className="card">
          <div className="form-group">
            <label className="form-label">Typ</label>
            <select className="form-control" value={form.typ_id} onChange={e => set('typ_id', e.target.value)}>
              <option value="">Kein Typ</option>
              {typen.filter(t => t.aktiv).map(t => <option key={t.id} value={t.id}>{t.icon} {t.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Titel <span style={{ color: 'red' }}>*</span></label>
            <input className="form-control" value={form.titel} maxLength={200}
              onChange={e => set('titel', e.target.value)} placeholder="z.B. Schwimmkurs Bronze" />
          </div>
          <div className="form-group">
            <label className="form-label">Untertitel <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
            <input className="form-control" value={form.untertitel} maxLength={300}
              onChange={e => set('untertitel', e.target.value)} placeholder="Kurze Zusatzinfo" />
          </div>
          <div className="form-group">
            <label className="form-label">Beschreibung</label>
            <textarea className="form-control" rows={5} value={form.beschreibung}
              onChange={e => set('beschreibung', e.target.value)}
              placeholder="Ausführliche Beschreibung des Angebots…" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Ort</label>
              <input className="form-control" value={form.ort} maxLength={200}
                onChange={e => set('ort', e.target.value)} placeholder="z.B. Hallenbad Grevenbroich" />
            </div>
            <div className="form-group">
              <label className="form-label">Kursleiter / Trainer</label>
              <input className="form-control" value={form.kursleiter} maxLength={200}
                onChange={e => set('kursleiter', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="entwurf">Entwurf</option>
                <option value="aktiv">Aktiv (sichtbar)</option>
                <option value="ausgebucht">Ausgebucht</option>
                <option value="abgesagt">Abgesagt</option>
                <option value="beendet">Beendet</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Spezielle E-Mail-Vorlage</label>
              <select className="form-control" value={form.email_vorlage_id} onChange={e => set('email_vorlage_id', e.target.value)}>
                <option value="">Allgemeine Vorlage verwenden</option>
                {emailVorlagen.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          </div>

          {/* Bild-Upload (nur im Bearbeitungsmodus) */}
          {isEdit && (
            <div className="form-group" style={{ marginTop: '0.5rem' }}>
              <label className="form-label">Bild</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                {bildPfad ? (
                  <img src={`/uploads/${bildPfad}`} alt="Vorschau"
                    style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
                ) : (
                  <div style={{ width: 96, height: 72, background: '#f0f4f8', borderRadius: 6, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '1.5rem' }}>
                    🖼
                  </div>
                )}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setBildDialog(true)}>
                  🖼 {bildPfad ? 'Bild ändern' : 'Bild wählen'}
                </button>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Neu hochladen oder aus der Bildbibliothek wählen · JPG, PNG, WebP bis 2 MB
                </span>
              </div>
              {bildDialog && (
                <BildAuswahl
                  titel={`Bild für „${form.titel || 'Angebot'}"`}
                  aktuellerPfad={bildPfad}
                  onAuswahl={handleBildAuswahl}
                  onClose={() => setBildDialog(false)}
                />
              )}
            </div>
          )}
          {!isEdit && (
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.5rem', fontStyle: 'italic' }}>
              💡 Ein Bild kann nach dem ersten Speichern gewählt werden.
            </p>
          )}
        </div>
      )}

      {/* ─── Step 1: Termine ────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="card">
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
            Ein Angebot kann einen oder mehrere Termine haben (z.B. Kursblöcke, Wochenfahrt usw.).
          </p>
          {termine.map((t, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '0.5rem', alignItems: 'end', marginBottom: '0.75rem', background: '#f8f9fa', padding: '0.75rem', borderRadius: 6 }}>
              <div>
                <label className="form-label">Datum</label>
                <input type="date" className="form-control" value={t.datum} onChange={e => setTermin(i, 'datum', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Von</label>
                <input type="time" className="form-control" value={t.uhrzeit_von} onChange={e => setTermin(i, 'uhrzeit_von', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Bis</label>
                <input type="time" className="form-control" value={t.uhrzeit_bis} onChange={e => setTermin(i, 'uhrzeit_bis', e.target.value)} />
              </div>
              <button className="btn btn-danger btn-sm" style={{ marginTop: '1.4rem' }} onClick={() => removeTermin(i)}>✕</button>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Bemerkung <span style={{ fontWeight: 400 }}>(optional)</span></label>
                <input className="form-control" value={t.bemerkung} maxLength={300}
                  placeholder="z.B. Nur für Anfänger" onChange={e => setTermin(i, 'bemerkung', e.target.value)} />
              </div>
            </div>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={addTermin}>+ Termin hinzufügen</button>
        </div>
      )}

      {/* ─── Step 2: Kapazität & Preis ──────────────────────────────────── */}
      {step === 2 && (
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Max. Teilnehmer</label>
              <input type="number" className="form-control" min={1} value={form.max_teilnehmer}
                onChange={e => set('max_teilnehmer', e.target.value)} placeholder="Unbegrenzt" />
            </div>
            <div className="form-group">
              <label className="form-label">Mindestalter</label>
              <input type="number" className="form-control" min={0} max={99} value={form.min_alter}
                onChange={e => set('min_alter', e.target.value)} placeholder="–" />
            </div>
            <div className="form-group">
              <label className="form-label">Höchstalter</label>
              <input type="number" className="form-control" min={0} max={99} value={form.max_alter}
                onChange={e => set('max_alter', e.target.value)} placeholder="–" />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0 1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: 6 }}>
            <input type="checkbox" id="wl" checked={form.warteliste_aktiv}
              onChange={e => set('warteliste_aktiv', e.target.checked)} style={{ width: 16, height: 16 }} />
            <label htmlFor="wl" style={{ margin: 0, fontSize: '0.9rem', cursor: 'pointer' }}>
              Warteliste aktivieren (wenn ausgebucht, Interessenten in Warteliste aufnehmen)
            </label>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '1rem 0' }} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Preis Mitglieder (€)</label>
              <input type="number" className="form-control" min={0} step="0.01" value={form.preis_mitglied}
                onChange={e => set('preis_mitglied', e.target.value)} placeholder="Kostenlos" />
            </div>
            <div className="form-group">
              <label className="form-label">Preis Nichtmitglieder (€)</label>
              <input type="number" className="form-control" min={0} step="0.01" value={form.preis_gast}
                onChange={e => set('preis_gast', e.target.value)} placeholder="Kostenlos" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Zahlungshinweis / IBAN-Info</label>
            <textarea className="form-control" rows={4} value={form.zahlungshinweis}
              onChange={e => set('zahlungshinweis', e.target.value)}
              placeholder="z.B.: Bitte überweise den Betrag auf IBAN: DE12 3456 7890 … Verwendungszweck: [Name] – [Angebot]" />
          </div>
        </div>
      )}

      {/* ─── Step 3: Formular-Baukasten ──────────────────────────────────── */}
      {step === 3 && (
        <div className="card">
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
            Kontakt (Name, E-Mail, Telefon) wird immer abgefragt. Zusätzliche Felder hier aktivieren:
          </p>

          <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Vordefinierte Felder
          </p>

          {felder.map(f => (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem',
              marginBottom: '0.4rem', borderRadius: 6,
              background: f.aktiv ? '#e8f5e9' : '#f8f9fa',
              border: `1px solid ${f.aktiv ? '#a5d6a7' : 'var(--border)'}`,
            }}>
              <input type="checkbox" checked={f.aktiv} onChange={() => toggleFeld(f.id)}
                style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }} />
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{f.label}</span>
                <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  [{TYP_LABEL[f.typ] || f.typ}]
                </span>
                {f.hinweis && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{f.hinweis}</div>}
              </div>
              {f.aktiv && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0 }}>
                  <input type="checkbox" checked={f.pflicht} onChange={e => setFeldPflicht(f.id, e.target.checked)}
                    style={{ width: 14, height: 14 }} />
                  Pflicht
                </label>
              )}
            </div>
          ))}

          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '1.25rem 0' }} />

          <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Eigene Felder
          </p>

          {customFelder.map(f => (
            <div key={f.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '0.5rem', alignItems: 'end', marginBottom: '0.75rem', background: '#f0f4f8', padding: '0.75rem', borderRadius: 6 }}>
              <div>
                <label className="form-label">Feldname / Frage</label>
                <input className="form-control" value={f.label} maxLength={200} placeholder="z.B. T-Shirt-Größe"
                  onChange={e => setCustomFeld(f.id, 'label', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Typ</label>
                <select className="form-control" value={f.typ} onChange={e => setCustomFeld(f.id, 'typ', e.target.value)}>
                  <option value="text">Text</option>
                  <option value="textarea">Textbox</option>
                  <option value="select">Auswahl</option>
                  <option value="boolean">Ja/Nein</option>
                  <option value="date">Datum</option>
                </select>
              </div>
              <div>
                <label className="form-label">Pflicht</label>
                <div style={{ display: 'flex', alignItems: 'center', height: 36 }}>
                  <input type="checkbox" checked={f.pflicht} onChange={e => setCustomFeld(f.id, 'pflicht', e.target.checked)}
                    style={{ width: 16, height: 16 }} />
                </div>
              </div>
              <button className="btn btn-danger btn-sm" style={{ marginTop: '1.4rem' }} onClick={() => removeCustomFeld(f.id)}>✕</button>
              {f.typ === 'select' && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Optionen (kommagetrennt)</label>
                  <input className="form-control" value={(f.optionen || []).join(', ')}
                    placeholder="Option A, Option B, Option C"
                    onChange={e => setCustomFeld(f.id, 'optionen', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
                </div>
              )}
            </div>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={addCustomFeld}>+ Eigenes Feld hinzufügen</button>
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
        <button className="btn btn-secondary" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}>
          ← Zurück
        </button>
        {step < STEPS.length - 1 ? (
          <button className="btn btn-primary" onClick={() => setStep(s => s + 1)}>
            Weiter →
          </button>
        ) : (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Wird gespeichert…' : (isEdit ? '✓ Änderungen speichern' : '✓ Angebot erstellen')}
          </button>
        )}
      </div>
    </div>
  );
}
